<template>
  <div class="aco-grid">
    <div class="aco-info-box">
      <b-card>
        <margin-fields :fields="marginFields()"/>
        <b-button type="submit" variant="primary" v-if="isSharedUser" block v-b-modal.show-password-modal>{{ $t('showPassword') }}</b-button>
        <show-user-password/>
        <b-button type="submit" variant="primary" v-if="isLocalUser" block v-b-modal.change-password-modal>{{ $t('changePassword') }}</b-button>
        <change-user-password/>
        <b-button type="submit" variant="danger" block v-b-modal.confirm-atb-delete>{{ $t('deleteAtbInfo') }}</b-button>
        <b-button type="submit" variant="danger" block v-b-modal.confirm-delete>{{ $t('deleteUser') }}</b-button>
        <b-button id="showEvents" type="submit" block :disabled="isEventShowButtonEnabled" v-on:click="showEvents()">{{ $t('showEvents') }}</b-button>
        <show-events ref="showEvents" :events="events"/>
        <b-button block v-on:click="licenseSearch()">{{ $t('licenseSearch') }}</b-button>
        <b-button block v-on:click="setActiveUser()" :disabled="isActiveProfileSet">{{ $t('setActiveUser') }}</b-button>
      </b-card>
    </div>

    <div>
      <div class="aco-details-box">
        <b-form autocomplete="off">
          <fieldset :disabled="loading">
            <b-col class="aco-details">
              <organization-search ref="searchOrganization"/>

              <details-form :fields="detailFields()" :state-object="user"/>

              <hr/>

              <h5>{{ $t('affiliationsAndRoles')}}</h5>

              <b-row>
                <b-col md="4">
                  <label>{{ $t('homeOrganization') }}</label>

                  <div class="aco-table" style="padding: 0.25em">
                    <div v-if="hasHomeOrganization">
                      <b-row>
                        <b-col md="3">{{ $t('organizationNumberAbbreviated') }}</b-col>
                        <b-col md="9">{{ user.homeOrganization.number }}</b-col>
                      </b-row>
                      <b-row>
                        <b-col md="3">{{ $t('name') }}</b-col>
                        <b-col md="9">{{ user.homeOrganization.name }}</b-col>
                      </b-row>
                    </div>
                    <div v-else>
                      {{ $t('notSet') }}
                    </div>
                  </div>
                  <b-button size="sm" class="float-left mt-3" @click="removeHomeOrganization" :disabled="!canEditOrganization || !hasHomeOrganization">
                    {{ $t('remove') }}
                  </b-button>
                  <b-button size="sm" class="float-right mt-3" @click="$refs['searchOrganization'].showModal(setHomeOrganization)" :disabled="!canEditOrganization">
                    {{ $t('change') }}
                  </b-button>
                </b-col>

                <b-col md="5">
                  <label>{{ $t('schools') }}</label>
                  <template>
                    <b-table show-empty striped hover small class="aco-table" :items="user.schoolOrganizations" :fields="schoolFields()">
                      <template slot="empty">
                        <div class="text-center">{{ $t('noResults') }}</div>
                      </template>
                      <template v-slot:cell(to_group)="row">
                        <b-button size="sm" class="py-0 float-right" @click="removeSchoolOrganization(row.item.number)" :disabled="!canEditOrganization">
                          {{ $t('remove') }}
                        </b-button>
                      </template>
                    </b-table>
                  </template>
                  <b-button size="sm" class="float-right mt-3" @click="$refs['searchOrganization'].showModal(addSchoolOrganization)" :disabled="!canEditOrganization">
                    {{ $t('add') }}
                  </b-button>
                </b-col>

                <b-col md="1">
                  <label>{{ $t('primaryRole') }}</label>
                  <b-form-select id="primaryRole" v-model="user.primaryRole" :options="userPrimaryRoles" :disabled="!enablePrimaryRoleChange" />
                </b-col>

                <b-col md="2">
                  <label>{{ $t('additionalRoles') }}</label>
                  <b-form-select id="additionalRoles" v-model="user.additionalRoles" :options="this.userAdditionalRoles"
                                 :multiple=true :select-size=6>
                    <b-tooltip target="additionalRoles">{{ $t('toolTip.userProfileRow') }}</b-tooltip>
                  </b-form-select>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="12">
                  <b-button type="button" class="float-left mt-3" @click="reloadUser()">{{ $t('reset') }}</b-button>
                  <b-button type="button" variant="primary" class="float-right mt-3" @click="saveUser()">{{ $t('save') }}</b-button>
                </b-col>
              </b-row>
            </b-col>
          </fieldset>
        </b-form>
      </div>

      <div class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col>
              <h5>{{ $t('groups') }}</h5>
            </b-col>
          </b-row>
          <template>
            <b-table show-empty striped hover small class="aco-table" :items="userGroups" :fields="userGroupFields()">
              <template slot="empty">
                <div class="text-center">{{ $t('noResults') }}</div>
              </template>
              <template v-slot:cell(to_group)="row">
                <router-link v-if="row.item.groupSource !== 'LOKUS'" :to="{ name: 'GroupDetails', params: { id: row.item.groupId }}">
                  <b-button size="sm" class="py-0">{{ $t('group') }}</b-button>
                </router-link>
              </template>
            </b-table>
          </template>
        </b-col>
      </div>

      <div class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col>
              <h5>{{ $t('userProfile') }}</h5>
            </b-col>
          </b-row>
          <b-form>
            <fieldset :disabled="userProfileLoading">
              <b-row id="userProfileRow">
                <div class="col-md-4">
                  <b> {{ $t('subjects') }} </b>
                  <b-form-select v-model="userProfile.subjects" :options=subjects multiple :select-size=subjects.length style="overflow: hidden;"/>
                </div>
                <div class="col-md-4">
                  <b> {{ $t('grades') }} </b>
                  <b-form-select v-model="userProfile.localGrades" :options=grades multiple :select-size=grades.length style="overflow: hidden;"/>
                  <b-row class="mt-4" v-if="isFeideUser">
                    <b-col>
                      <b>{{ $t('feideGrades') }}</b>
                    </b-col>
                    <b-col md="12">
                      <span v-for="(grade) in userProfile.feideGrades" v-bind:key="grade">{{ translateGrades(grade) }}<br/></span></b-col>
                  </b-row>
                </div>
                <div class="col-md-4">
                  <b> {{ $t('preferredPrograms') }} </b>
                  <b-form-select v-model="userProfile.preferredPrograms" :options="preferredPrograms" multiple :select-size="preferredPrograms.length" style="overflow: hidden;"/>
                </div>
                <b-tooltip target="userProfileRow">{{ $t('toolTip.userProfileRow') }}</b-tooltip>
              </b-row>
              <b-row>
                <b-col md="12">
                  <b-button type="button" class="float-left mt-3" @click="resetUserProfile">{{ $t('reset') }}</b-button>
                  <b-button type="button" variant="primary" class="float-right mt-3" @click="updateUserProfile()">{{ $t('save') }}</b-button>
                </b-col>
              </b-row>
            </fieldset>
          </b-form>
        </b-col>
      </div>
    </div>

    <b-modal id="confirm-delete" no-stacking>
      <template slot="modal-title">{{ $t('confirmationTitle') }}</template>
      <p>{{ $t('confirmDelete') }}</p>
      <template slot="modal-footer">
        <b-button variant="danger" v-on:click="deleteUser(false)">
          {{ $t('delete') }}
        </b-button>
        <b-button variant="secondary" class="float-lg-right" @click="$bvModal.hide('confirm-delete')">
          {{ $t('cancel') }}
        </b-button>
      </template>
    </b-modal>

    <b-modal id="confirm-force-delete" @ok="deleteUser(true)" ok-variant="outline-primary" no-close-on-esc no-close-on-backdrop>
      <template slot="modal-title">{{ $t('confirmationTitle') }}</template>
      <template slot="modal-ok">{{ $t('delete') }}</template>
      <template slot="modal-cancel"> {{ $t('cancel') }}</template>
      <template slot="default">{{ $t('confirmUserDelete') }}</template>
    </b-modal>

    <b-modal id="confirm-atb-delete" no-stacking>
      <template slot="modal-title">{{ $t('confirmationTitle') }}</template>
      <p>{{ $t('deleteAtbInfoConfirmation') }}</p>
      <template slot="modal-footer">
        <b-button variant="danger" v-on:click="deleteAtbInfo">
          {{ $t('delete') }}
        </b-button>
        <b-button variant="secondary" class="float-lg-right" @click="$bvModal.hide('confirm-atb-delete')">
          {{ $t('cancel') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import BaseComponent from '../../components/BaseComponent'
  import { mapGetters } from 'vuex'
  import ChangeUserPassword from './ChangeUserPassword'
  import ShowUserPassword from './ShowUserPassword'
  import UserService from '../../services/UserService'
  import OrganizationSearch from '../../components/OrganizationSearch'
  import MarginFields from '../../components/MarginFields'
  import DetailsForm from '../../components/DetailsForm'
  import ShowEvents from '../../components/ShowEvents'
  import { isCustomerAdministrator } from '@/services/PermissionService';

  export default {
    name: 'UserDetails',
    extends: BaseComponent,

    components: {
      ChangeUserPassword,
      ShowUserPassword,
      OrganizationSearch,
      MarginFields,
      DetailsForm,
      ShowEvents
    },
    data () {
      return {}
    },

    computed: {
      ...mapGetters('oidcStore', ['oidcUser']),
      ...mapGetters('users', ['user', 'userGroups', 'isEventShowButtonEnabled', 'events', 'loading']),
      ...mapGetters('userProfiles', ['userProfile', 'userProfileLoading']),
      ...mapGetters('languageStore', ['langCode']),
      ...mapGetters('sessionStorage', ['isActiveProfileSet', 'activeProfile']),
      isSharedUser () {
        return (this.user.source === 'LOCAL' && this.user.additionalRoles.includes('shared_user'))
      },
      isLocalUser () {
        return (this.user.source === 'LOCAL')
      },
      isFeideUser () {
        return (this.user.source === 'FEIDE')
      },
      enablePrimaryRoleChange() {
        return this.user && this.canEditOrganization && this.oidcUser && isCustomerAdministrator(this.oidcUser.username);
      },
      hasHomeOrganization() {
        return this.user && this.user?.homeOrganization;
      },
      canEditOrganization() {
        return this.user && ['LOCAL', 'VIPPS'].includes(this.user.source)
      },
    },

    created: function () {
      this.$store.dispatch('users/getUser', this.$route.params.id)
      this.$store.dispatch('userProfiles/getUserProfile', this.$route.params.id)
      this.$store.dispatch('users/getGroupsByUserId', this.$route.params.id)
      const objectReference = 'user:userId:' + this.$route.params.id
      this.$store.dispatch('users/getObjectReference', objectReference)
    },

    methods: {
      deleteUser (force) {
        let that = this
        let request = {
          userId: this.$route.params.id,
          force: force
        }
        UserService.deleteUser(request)
          .then(function () {
            that.$bvModal.hide('confirm-delete')
            that.$router.go(-1)
          })
          .catch(error => {
            if (error.response.status === 409) {
              that.$bvModal.hide('confirm-delete')
              that.$bvModal.show('confirm-force-delete')
            }
          })
      },
      deleteAtbInfo () {
        this.$bvModal.hide('confirm-atb-delete')
        this.$store.dispatch('users/deleteAtbInfo', this.$route.params.id)
      },
      marginFields () {
        return [
          { name: 'source', displayName: this.$t('source'), value: this.translateAlternatives('userSources', this.user.source) },
          { name: 'userId', displayName: this.$t('userId'), value: this.user.userId },
          { name: 'lastLogin', displayName: this.$t('lastLogin'), value: this.formatTimestamp(this.user.lastLogin) },
          { name: 'created', displayName: this.$t('created'), value: this.formatTimestamp(this.user.created) },
          { name: 'createdBy', displayName: this.$t('createdBy'), value: this.user.createdBy },
          { name: 'modified', displayName: this.$t('modified'), value: this.formatTimestamp(this.user.modified) },
          { name: 'modifiedBy', displayName: this.$t('modifiedBy'), value: this.user.modifiedBy },
        ]
      },
      detailFields () {
        return [
          { type: 'header', backButton: true, width: 12, displayName: this.$t('userInformation') },
          { type: 'text', name: 'username', width: 3, readonly: !this.isLocalUser },
          { type: 'text', name: 'fullName', width: 3, readonly: !this.isLocalUser },
          { type: 'text', name: 'firstName', width: 3, readonly: !this.isLocalUser },
          { type: 'text', name: 'lastName', width: 3, readonly: !this.isLocalUser },
          { type: 'text', name: 'email', width: 3, readonly: false },
          { type: 'text', name: 'mobile', width: 2, readonly: !this.isLocalUser },
          { type: 'dropdown', name: 'preferredLanguage', width: 2, items: this.languages },
          { type: 'date', name: 'expiryDate', width: 2, displayName: this.$t('expiryDate') },
          { type: 'boolean', name: 'active', width: 1 },
        ]
      },
      userGroupFields () {
        return [
          { key: 'name', label: this.$t('name'), tdClass: 'text-nowrap' },
          { key: 'groupSource', label: this.$t('source'), tdClass: 'text-nowrap', formatter: value => this.translateAlternatives('groupSources', value) },
          { key: 'groupType', label: this.$t('type'), tdClass: 'text-nowrap', formatter: value => this.translateAlternatives('groupTypes', value) },
          { key: 'grepCode', label: this.$t('grepFagkode'), tdClass: 'text-nowrap' },
          { key: 'userRole', label: this.$t('role'), tdClass: 'text-nowrap', formatter: value => this.translateAlternatives('userPrimaryRoles', value) },
          { key: 'ownerOrganizationNumber', label: this.$t('organization'), tdClass: 'text-nowrap' },
          { key: 'startDate', label: this.$t('startDate'), tdClass: 'text-nowrap' },
          { key: 'endDate', label: this.$t('endDate'), tdClass: 'text-nowrap' },
          { key: 'to_group', label: '' }
        ]
      },
      schoolFields () {
        return [
          { key: 'number', label: this.$t('organizationNumber'), tdClass: 'text-nowrap' },
          { key: 'name', label: this.$t('name'), tdClass: 'text-nowrap' },
          { key: 'to_group', label: '' }
        ]
      },
      saveUser () {
        this.$store.dispatch('users/updateUser', this.$route.params.id)
      },
      reloadUser () {
        this.$store.dispatch('users/getUser', this.$route.params.id)
      },
      updateUserProfile () {
        this.$store.dispatch('userProfiles/updateUserProfile', this.$route.params.id)
      },
      resetUserProfile () {
        this.$store.dispatch('userProfiles/getUserProfile', this.$route.params.id)
      },
      setHomeOrganization (homeOrganization) {
        let organization = {
          'name': homeOrganization.name,
          'number': homeOrganization.organizationNumber
        }
        this.$store.dispatch('users/updateHomeOrganization', organization)
      },
      removeHomeOrganization () {
        this.$store.dispatch('users/updateHomeOrganization', null)
      },
      addSchoolOrganization (organizationToAdd) {
        let organization = {
          'name': organizationToAdd.name,
          'number': organizationToAdd.organizationNumber
        }
        this.$store.dispatch('users/addSchoolOrganization', organization)
      },
      removeSchoolOrganization (organizationNumber) {
        this.$store.dispatch('users/removeSchoolOrganization', organizationNumber)
      },
      showEvents () {
        this.$root.$emit('bv::show::modal', 'showEvents')
      },
      licenseSearch () {
        this.$store.dispatch('licenses/resetFilter')
        let payload = {
          customerUserId: this.$route.params.id,
        }
        this.$store.dispatch('licenses/getLicensesByCustomerOrgNumberOrUserIdOrEan', payload)
          .then(() => {
            this.$router.push('/licenses')
          })
      },
      setActiveUser () {
        let sessionStorageRequest = {
          activeProfileType: 'user',
          id: this.$route.params.id,
          name: this.user.fullName
        }
        this.$store.dispatch('sessionStorage/updateSessionStorage', sessionStorageRequest)
        this.$store.dispatch('sessionStorage/updateState')
      }
    },
    i18n: {
      messages: {
        en: {
          'affiliationsAndRoles': 'Affiliations and roles',
          'showPassword': 'Show password',
          'deleteAtbInfo': 'Delete ATB info',
          'deleteUser': 'Delete user',
          'setActiveUser': 'Set as active user',
          'homeOrganization': 'Home organization',
          'userProfile': 'User profile',
          'feideGrades': 'Grades received from Feide',
          'confirmUserDelete': 'Are you sure you want to delete this user? User has one or more active licenses.',
          'deleteAtbInfoConfirmation': 'Are you sure you want to delete the ATB connection? This will delete any notes the user has added to Active Textbook',
          'lastLogin': 'Last login',
          'expiryDate': 'Expiry date',
          'grepFagkode': 'Fagkode',
          'preferredPrograms': 'Preferred programs',
        },
        nb: {
          'affiliationsAndRoles': 'Tilhørighet og roller',
          'showPassword': 'Vis passord',
          'deleteAtbInfo': 'Slett ATB info',
          'deleteUser': 'Delete user',
          'setActiveUser': 'Sett aktiv bruker',
          'homeOrganization': 'Hjemmeorganisasjon',
          'userProfile': 'Brukerprofil',
          'feideGrades': 'Årstrinn mottatt fra Feide',
          'confirmUserDelete': 'Er du sikker på at du vil slette brukeren? Bruker har en eller flere aktive lisenser.',
          'deleteAtbInfoConfirmation': 'Er du sikker på at du vil slette ATB-tilknytning? Dette vil slette eventuelle notater brukeren har lagt inn i en Active Textbook',
          'lastLogin': 'Sist innlogget',
          'expiryDate': 'Utløpsdato',
          'grepFagkode': 'Fagkode',
          'preferredPrograms': 'Studieprogram',
        }
      }
    }
  }
</script>
